// Fedi Memories — entrypoint frontend (M11 · redesign Material 3, Pacchetto A).
// Flusso: verifica sessione -> login oppure mappa. Private mode via PIN inserito
// nella search bar (routing locale, nessun geocoder). La decisione di privacy
// resta sempre backend-side.

import "./styles/main.css";
import {
  ApiError,
  deletePhoto,
  getSession,
  listPhotos,
  lockPrivate,
  login,
  logout,
  mediaUrl,
  unlockPrivate,
  updatePhoto,
  type PhotoSummary,
  type PrivateModeInfo,
} from "./lib/api";
import { createMap, renderMarkers } from "./lib/map";
import { icon } from "./lib/icons";
import { CATEGORIES } from "./lib/categories";
import { startIdleWatch } from "./lib/idle";
import { escapeHtml, formatDate, formatTime, toDatetimeLocal } from "./lib/format";
import { renderTimeline } from "./lib/timeline-view";
import { searchPhotos } from "./lib/search";
import { parsePlaceLabel } from "./lib/place";
import { enableSwipeToClose } from "./lib/ui/sheet";
import { categorySelectMarkup, locationPickerMarkup } from "./lib/ui/markup";
import { wireCategorySelect, wireDropdownToggle } from "./lib/ui/select";
import { createLocationPicker, type LocationPicker } from "./lib/ui/location-picker";
import { mountUpload } from "./lib/ui/upload";

// Temi colore (UI-only): variano i token CSS via attributo data-theme sul
// documento. Nessun impatto su logica/dati/private mode, solo presentazione.
type ThemeId = "locus" | "atlante" | "minimal";
const THEME_STORAGE_KEY = "locus-theme";
const THEMES: { id: ThemeId; label: string; preview: string }[] = [
  { id: "locus", label: "Locus", preview: "#6B4C3B" },
  { id: "atlante", label: "Atlante", preview: "#2C4A3E" },
  { id: "minimal", label: "Minimal", preview: "#2B2826" },
];

function getStoredTheme(): ThemeId {
  const v = localStorage.getItem(THEME_STORAGE_KEY);
  return v === "atlante" || v === "minimal" || v === "locus" ? v : "locus";
}

function applyTheme(id: ThemeId): void {
  document.documentElement.setAttribute("data-theme", id);
  localStorage.setItem(THEME_STORAGE_KEY, id);
}

// Applicato subito, prima di qualsiasi render, per evitare flash del tema default.
applyTheme(getStoredTheme());

/** Custom dropdown single-select per il tema: trigger con swatch + nome del tema
 * attivo, panel con swatch e nome per ogni tema. Si chiude alla selezione. */
function themeSelectMarkup(current: ThemeId): string {
  const cur = THEMES.find((t) => t.id === current)!;
  const options = THEMES.map((t) => {
    const on = t.id === current;
    return `<button type="button" class="select__option${on ? " is-selected" : ""}" role="option" aria-selected="${on}" data-theme-id="${t.id}">
        <span class="select__swatch" style="background:${t.preview}" aria-hidden="true"></span>
        <span class="select__option-label">${t.label}</span>
        <span class="select__tick" aria-hidden="true">${icon("check", { size: 18 })}</span>
      </button>`;
  }).join("");
  return `<div class="select" id="themeSelect" data-open="false">
      <button type="button" class="select__trigger" aria-haspopup="listbox" aria-expanded="false">
        <span class="select__value select__value--theme">
          <span class="select__swatch" style="background:${cur.preview}" aria-hidden="true"></span>
          <span>${cur.label}</span>
        </span>
        ${icon("expand_more", { size: 20, cls: "select__chevron" })}
      </button>
      <div class="select__panel" role="listbox" aria-label="Tema" hidden>
        ${options}
      </div>
    </div>`;
}

/** Markup dei toggle gender (icon-only). Reso solo in private mode dai chiamanti. */
function genderToggles(male = false, female = false): string {
  const btn = (g: "male" | "female", label: string, on: boolean): string =>
    `<button type="button" class="chip chip--icon${on ? " chip--selected" : ""}" data-gender="${g}" aria-pressed="${on}" aria-label="${label}" title="${label}">${icon(g, { size: 20 })}</button>`;
  return `${btn("male", "Maschile", male)}${btn("female", "Femminile", female)}`;
}

/** Abilita il toggle dei chip (categorie + gender) in un container. */
function wireChips(container: HTMLElement): void {
  container.querySelectorAll<HTMLButtonElement>("[data-cat],[data-gender]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const on = btn.getAttribute("aria-pressed") === "true";
      btn.setAttribute("aria-pressed", on ? "false" : "true");
      btn.classList.toggle("chip--selected", !on);
    });
  });
}

/** Valori selezionati per un attributo data-* nei chip di un container. */
function selectedChips(container: HTMLElement | null, attr: "cat" | "gender"): string[] {
  if (!container) return [];
  return [...container.querySelectorAll<HTMLButtonElement>(`[data-${attr}][aria-pressed="true"]`)].map(
    (b) => b.dataset[attr] ?? "",
  );
}

const app = document.querySelector<HTMLDivElement>("#app")!;
const qs = <T extends Element>(sel: string) => document.querySelector<T>(sel)!;

/** Dialog di conferma nativo (sostituisce window.confirm): risolve true su "Elimina". */
function confirmDialog(message: string): Promise<boolean> {
  const dialog = qs<HTMLDialogElement>("#confirmDialog");
  dialog.querySelector<HTMLParagraphElement>(".confirm-dialog__msg")!.textContent = message;
  dialog.showModal();
  return new Promise((resolve) => {
    dialog.addEventListener("close", () => resolve(dialog.returnValue === "confirm"), { once: true });
  });
}

async function boot(): Promise<void> {
  try {
    const session = await getSession();
    if (session.authenticated) renderApp(session.privateMode ?? { active: false });
    else renderLogin();
  } catch {
    renderLogin("Errore di rete. Riprova.");
  }
}

function renderLogin(message?: string): void {
  app.innerHTML = `
    <div class="login">
      <form class="login__card" id="loginForm">
        <div class="login__brand">
          <img class="login__logo-img" src="/brand/locus/locus-logo-stacked-light.svg" alt="Locus" />
        </div>
        <label class="field">
          <span class="field__label">Username</span>
          <input class="field__input" name="username" autocomplete="username" required />
        </label>
        <label class="field">
          <span class="field__label">Password</span>
          <input class="field__input" name="password" type="password" autocomplete="current-password" required />
        </label>
        <button type="submit" class="btn" id="loginSubmit">Entra</button>
        <p class="login__msg" id="loginMsg">${message ? escapeHtml(message) : ""}</p>
      </form>
    </div>`;

  const form = qs<HTMLFormElement>("#loginForm");
  const msg = qs<HTMLParagraphElement>("#loginMsg");
  const button = qs<HTMLButtonElement>("#loginSubmit");
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const data = new FormData(form);
    button.disabled = true;
    msg.classList.add("login__msg--info");
    msg.textContent = "Accesso in corso…";
    try {
      await login(String(data.get("username")), String(data.get("password")));
      const session = await getSession();
      renderApp(session.privateMode ?? { active: false });
    } catch (err) {
      button.disabled = false;
      msg.classList.remove("login__msg--info");
      msg.textContent =
        err instanceof ApiError && err.status === 401
          ? "Credenziali non valide."
          : "Errore durante l'accesso.";
    }
  });
}

function renderApp(initialPrivate: PrivateModeInfo): void {
  // Filtri categoria (client-side, sui dati già caricati), filtro privacy e tema colore attivo.
  const activeCategoryFilters: string[] = [];
  let privateOnlyFilter = false;
  let currentTheme: ThemeId = getStoredTheme();

  app.innerHTML = `
    <div id="map" class="map"></div>
    <div id="timeline" class="timeline" hidden></div>
    <div id="empty" class="empty" hidden>
      <div class="empty__card">
        <img class="empty__symbol" src="/brand/locus/locus-symbol-light.svg" alt="" aria-hidden="true" />
        <h2 id="emptyTitle" class="empty__title">La tua mappa è vuota</h2>
        <p id="emptyText" class="empty__text">Carica la tua prima foto per iniziare a riempire la mappa dei ricordi.</p>
      </div>
    </div>
    <header class="appbar">
      <div class="appbar__brand">
        <img class="appbar__logo" src="/brand/locus/locus-logo-horizontal-light.svg" alt="Locus" />
      </div>
      <form id="searchForm" class="searchbar" role="search">
        ${icon("search", { cls: "searchbar__icon" })}
        <input id="searchInput" class="searchbar__input" type="text" inputmode="search"
               autocomplete="off" placeholder="Cerca nei ricordi" aria-label="Cerca nei ricordi" />
      </form>
      <div class="appbar__icons">
        <button id="viewToggle" class="icon-btn" title="Timeline" aria-label="Mostra la timeline">${icon("photo", { label: "Timeline" })}</button>
        <button id="filtersBtn" class="icon-btn" title="Filtri" aria-label="Filtri" aria-haspopup="dialog">${icon("filter_list", { label: "Filtri" })}</button>
        <button id="settingsBtn" class="icon-btn" title="Impostazioni" aria-label="Impostazioni" aria-haspopup="dialog">${icon("settings", { label: "Impostazioni" })}</button>
        <button id="logoutBtn" class="icon-btn" title="Esci" aria-label="Esci">${icon("logout", { label: "Esci" })}</button>
      </div>
    </header>
    <div id="privateStatus" class="private-status"></div>
    <div id="status" class="snackbar" hidden></div>
    <div id="scrim" class="scrim" hidden></div>
    <div id="upload" class="sheet" hidden></div>
    <div id="detail" class="sheet" hidden></div>
    <div id="lightbox" class="lightbox" hidden></div>
    <div class="fab-wrap">
      <button id="uploadBtn" class="fab" title="Carica foto" aria-label="Carica foto">${icon("add", { label: "Carica foto" })}</button>
    </div>
    <dialog id="confirmDialog" class="dialog">
      <form method="dialog">
        <h2 class="dialog__title">Eliminare la foto?</h2>
        <p class="dialog__text confirm-dialog__msg"></p>
        <div class="dialog__actions">
          <button value="cancel" class="btn btn--text">Annulla</button>
          <button value="confirm" class="btn btn--danger">Elimina</button>
        </div>
      </form>
    </dialog>
    <dialog id="filtersDialog" class="side-sheet">
      <form method="dialog" class="side-sheet__form">
        <div class="dialog__head">
          <h2 class="dialog__title">Filtri</h2>
          <button value="close" class="icon-btn" title="Chiudi" aria-label="Chiudi">${icon("close", { label: "Chiudi" })}</button>
        </div>
        <div class="side-sheet__body">
          <p class="dialog__text">Mostra solo le foto di una o più categorie.</p>
          <div class="field">
            <span class="field__label">Categorie</span>
            ${categorySelectMarkup("filterCats", activeCategoryFilters)}
          </div>
          <div id="filterPrivacyField"></div>
        </div>
        <div class="dialog__actions">
          <button type="button" id="filtersClear" class="btn btn--text">Azzera</button>
          <button value="close" class="btn">Chiudi</button>
        </div>
      </form>
    </dialog>
    <dialog id="settingsDialog" class="side-sheet">
      <form method="dialog" class="side-sheet__form">
        <div class="dialog__head">
          <h2 class="dialog__title">Impostazioni</h2>
          <button value="close" class="icon-btn" title="Chiudi" aria-label="Chiudi">${icon("close", { label: "Chiudi" })}</button>
        </div>
        <div class="side-sheet__body">
          <p class="dialog__text">Scegli un tema colore (solo su questo dispositivo).</p>
          <div class="field">
            <span class="field__label">Tema</span>
            ${themeSelectMarkup(currentTheme)}
          </div>
        </div>
        <div class="dialog__actions">
          <button value="close" class="btn">Chiudi</button>
        </div>
      </form>
    </dialog>`;

  qs<HTMLButtonElement>("#logoutBtn").addEventListener("click", async () => {
    teardown = true;
    stopIdleWatch?.();
    forceCloseAllOverlays();
    try {
      await logout();
    } finally {
      renderLogin();
    }
  });

  // --- Filtri (client-side, sui dati già caricati: nessuna nuova API) ---
  qs<HTMLButtonElement>("#filtersBtn").addEventListener("click", () => {
    qs<HTMLDialogElement>("#filtersDialog").showModal();
  });

  // Dropdown categorie del filtro (multi-select con checkbox + icone): a ogni
  // cambio sincronizza activeCategoryFilters e ridisegna i marker.
  const filterCatSelect = wireCategorySelect(
    qs<HTMLDivElement>("#filterCats"),
    qs<HTMLDialogElement>("#filtersDialog"),
    () => {
      activeCategoryFilters.length = 0;
      activeCategoryFilters.push(...filterCatSelect.getSelected());
      render();
    },
  );

  qs<HTMLButtonElement>("#filtersClear").addEventListener("click", () => {
    activeCategoryFilters.length = 0;
    privateOnlyFilter = false;
    filterCatSelect.clear();
    const sw = document.querySelector<HTMLInputElement>("#filterPrivate");
    if (sw) sw.checked = false;
    render();
  });

  // --- Impostazioni: tema colore (UI-only, salvato solo in localStorage) ---
  qs<HTMLButtonElement>("#settingsBtn").addEventListener("click", () => {
    qs<HTMLDialogElement>("#settingsDialog").showModal();
  });

  function wireThemeSelect(): void {
    const root = qs<HTMLDivElement>("#themeSelect");
    const setOpen = wireDropdownToggle(root, qs<HTMLDialogElement>("#settingsDialog"));

    function updateThemeUI(): void {
      const cur = THEMES.find((t) => t.id === currentTheme)!;
      const valueEl = root.querySelector<HTMLSpanElement>(".select__value")!;
      valueEl.innerHTML = `<span class="select__swatch" style="background:${cur.preview}" aria-hidden="true"></span><span>${cur.label}</span>`;
      root.querySelectorAll<HTMLButtonElement>("[data-theme-id]").forEach((o) => {
        const on = o.dataset.themeId === currentTheme;
        o.setAttribute("aria-selected", String(on));
        o.classList.toggle("is-selected", on);
      });
    }

    root.querySelectorAll<HTMLButtonElement>("[data-theme-id]").forEach((opt) => {
      opt.addEventListener("click", () => {
        setOpen(false); // single-select: chiude alla scelta
        const id = opt.dataset.themeId as ThemeId;
        if (id === currentTheme) return;
        currentTheme = id;
        applyTheme(id);
        updateThemeUI();
        render();
      });
    });
  }
  wireThemeSelect();

  const mapEl = qs<HTMLDivElement>("#map");
  const map = createMap(mapEl);

  // Stato locale (solo per la UI; la verità è backend-side).
  let privateMode: PrivateModeInfo = initialPrivate;
  let markerLayer: ReturnType<typeof renderMarkers> | null = null;
  let photos: PhotoSummary[] = [];
  // Vista attiva: mappa (default, map-first) o timeline. Solo presentazione:
  // entrambe leggono lo stesso array `photos` già filtrato backend-side.
  let currentView: "map" | "timeline" = "map";
  // Query di ricerca (client-side, live). Filtra lo stesso `photos` già caricato
  // → si applica a mappa e timeline; privacy ereditata dal dataset.
  let searchQuery = "";
  // true mentre l'edit mostra il location picker: la mappa deve restare
  // cliccabile, quindi niente scrim sopra (stessa logica dell'upload).
  let editingPosition = false;
  // true dopo logout (manuale o per idle timeout): blocca repaint di risposte
  // in-flight che potrebbero ripopolare la UI con dati (anche privati) dopo
  // che la sessione non è più valida.
  let teardown = false;
  // Riferimenti per la pulizia forzata su idle timeout: l'edit della posizione
  // e la lightbox non espongono il proprio "closer" all'esterno della funzione
  // che li apre, quindi si tracciano qui in scrittura per poterli chiudere da
  // un punto centrale (forceCloseAllOverlays) senza duplicare la logica.
  let editLocationPicker: LocationPicker | null = null;
  let lightboxKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  let stopIdleWatch: (() => void) | null = null;

  function setStatus(text: string | null): void {
    const el = qs<HTMLDivElement>("#status");
    el.hidden = !text;
    el.textContent = text ?? "";
  }

  // Lo scrim modale copre la mappa per il detail, ma NON durante l'edit della
  // posizione: in quel caso la mappa resta cliccabile per scegliere il punto,
  // come già avviene per l'upload (bottom sheet non modale).
  function updateScrim(): void {
    const detailOpen = !qs<HTMLDivElement>("#detail").hidden;
    qs<HTMLDivElement>("#scrim").hidden = !detailOpen || editingPosition;
  }

  qs<HTMLDivElement>("#scrim").addEventListener("click", () => {
    qs<HTMLDivElement>("#detail").hidden = true;
    updateScrim();
  });

  /** Render dei marker dallo stato locale `photos` (filtrati per categoria e privacy) + toggle dell'empty state. */
  function render(): void {
    // Guardia contro risposte in-flight risolte dopo logout/idle timeout: senza
    // questo controllo una `refresh()` partita prima del teardown potrebbe
    // ripopolare marker (anche privati) dopo che la sessione non è più valida.
    if (teardown) return;
    const empty = qs<HTMLDivElement>("#empty");
    const emptyTitle = qs<HTMLHeadingElement>("#emptyTitle");
    const emptyText = qs<HTMLParagraphElement>("#emptyText");
    if (markerLayer) {
      markerLayer.remove();
      markerLayer = null;
    }
    let filtered = photos;
    if (activeCategoryFilters.length) {
      filtered = filtered.filter((p) => p.categories.some((c) => activeCategoryFilters.includes(c)));
    }
    if (privateOnlyFilter) {
      filtered = filtered.filter((p) => p.is_private === 1);
    }
    // Ricerca testuale (client-side, pura): ulteriore filtro nello stesso
    // pipeline → si applica sia a mappa sia a timeline. Query vuota/tutta-cifre
    // è no-op (vedi searchPhotos).
    filtered = searchPhotos(filtered, searchQuery);
    const timelineEl = qs<HTMLDivElement>("#timeline");
    // Copy neutra: nessun riferimento a "pubblico/privato" (no disclosure).
    if (photos.length === 0) {
      emptyTitle.textContent = "La tua mappa è vuota";
      emptyText.textContent = "Carica la tua prima foto per iniziare a riempire la mappa dei ricordi.";
      empty.hidden = false;
      timelineEl.innerHTML = "";
      return;
    }
    if (filtered.length === 0) {
      emptyTitle.textContent = "Nessuna foto per questo filtro";
      emptyText.textContent = "Azzera i filtri per tornare a vedere tutte le foto.";
      empty.hidden = false;
      timelineEl.innerHTML = "";
      return;
    }
    empty.hidden = true;
    if (currentView === "timeline") {
      renderTimeline(timelineEl, filtered, new Date(), { onSelect: (p) => openGroup([p]) });
    } else {
      markerLayer = renderMarkers(map, filtered, openGroup);
    }
  }

  // Toggle Mappa / Timeline (solo presentazione). Mostra/nasconde i container e
  // ridisegna; al ritorno sulla mappa invalida la size di Leaflet (il container
  // era display:none e va riallineato).
  function applyView(): void {
    const timelineEl = qs<HTMLDivElement>("#timeline");
    const toggle = qs<HTMLButtonElement>("#viewToggle");
    if (currentView === "timeline") {
      mapEl.hidden = true;
      timelineEl.hidden = false;
      toggle.title = "Mappa";
      toggle.setAttribute("aria-label", "Mostra la mappa");
      toggle.innerHTML = icon("location", { label: "Mappa" });
    } else {
      timelineEl.hidden = true;
      mapEl.hidden = false;
      toggle.title = "Timeline";
      toggle.setAttribute("aria-label", "Mostra la timeline");
      toggle.innerHTML = icon("photo", { label: "Timeline" });
      map.invalidateSize();
    }
    render();
  }

  qs<HTMLButtonElement>("#viewToggle").addEventListener("click", () => {
    currentView = currentView === "map" ? "timeline" : "map";
    applyView();
  });

  async function refresh(): Promise<void> {
    setStatus("Caricamento foto…");
    try {
      photos = await listPhotos();
      setStatus(null);
      render();
    } catch {
      setStatus("Errore nel caricamento delle foto.");
    }
  }

  // --- Search bar ---
  // Due funzioni sulla stessa barra, senza ambiguità:
  //  - testo → RICERCA nei ricordi (live, client-side, su `photos`): vedi il
  //    listener `input` qui sotto e il filtro `searchPhotos` in render();
  //  - input tutto-cifre al submit → candidato PIN per lo sblocco private mode.
  // La ricerca per luogo/indirizzo (geocoder) resta rimandata al Pacchetto F.

  // Ricerca live: a ogni digitazione aggiorna lo stato e ridisegna (mappa o
  // timeline, stesso pipeline). Una query tutta-cifre è no-op per la ricerca
  // (searchPhotos), così resta riservata allo sblocco PIN sul submit.
  qs<HTMLInputElement>("#searchInput").addEventListener("input", (e) => {
    searchQuery = (e.target as HTMLInputElement).value;
    render();
  });

  qs<HTMLFormElement>("#searchForm").addEventListener("submit", async (e) => {
    e.preventDefault();
    const input = qs<HTMLInputElement>("#searchInput");
    const q = input.value.trim();
    if (!q) return;

    // Routing locale: un input tutto-cifre è un candidato PIN e viene provato
    // SOLO contro il nostro endpoint /api/private/unlock — mai inviato a servizi
    // esterni. In Pacchetto A non esiste alcun geocoder, quindi non c'è rischio
    // di inoltro a terzi.
    if (/^\d{3,12}$/.test(q)) {
      try {
        const res = await unlockPrivate(q);
        privateMode = res.privateMode;
        input.value = "";
        searchQuery = "";
        renderPrivateControl();
        renderFilterPrivacyField();
        await refresh();
        return;
      } catch {
        // PIN errato oppure semplice query numerica: messaggio neutro, nessuna
        // disclosure dell'esistenza di una modalità privata.
        setStatus("Nessun risultato.");
        return;
      }
    }

    // Testo non numerico: la ricerca è già applicata live dal listener `input`;
    // il submit non ha azioni aggiuntive (niente geocoder in questo MVP).
  });

  function renderPrivateControl(): void {
    const ctl = qs<HTMLDivElement>("#privateStatus");
    if (privateMode.active) {
      const until = formatTime(privateMode.expiresAt);
      ctl.innerHTML = `
        <span class="private-chip" title="Modalità privata attiva">
          ${icon("lock_open")} Private${until ? ` · ${until}` : ""}
        </span>
        <button id="lockBtn" class="icon-btn" title="Blocca" aria-label="Blocca modalità privata">${icon("lock", { label: "Blocca" })}</button>`;
      qs<HTMLButtonElement>("#lockBtn").addEventListener("click", async () => {
        try {
          const res = await lockPrivate();
          privateMode = res.privateMode;
        } finally {
          // Lock manuale: stessa pulizia forzata dell'idle timeout, per non
          // lasciare foto private esposte in detail/lightbox/upload anche se
          // la chiamata di lock fallisce. refresh() ripopola subito dopo con
          // la vista post-lock.
          forceCloseAllOverlays();
          privateOnlyFilter = false;
          renderPrivateControl();
          renderFilterPrivacyField();
          await refresh();
        }
      });
    } else {
      // Pre-unlock: nessun indizio dell'esistenza di una modalità privata.
      ctl.innerHTML = "";
    }
  }

  /** Sezione "Visualizzazione" del filtro (toggle solo-private): ricostruita
   * a ogni cambio di privateMode (non solo al primo render di renderApp),
   * altrimenti resta nascosta dopo un unlock fatto senza ricaricare la pagina. */
  function renderFilterPrivacyField(): void {
    const field = qs<HTMLDivElement>("#filterPrivacyField");
    if (!privateMode.active) {
      field.innerHTML = "";
      return;
    }
    field.innerHTML = `
      <div class="field">
        <span class="field__label">Visualizzazione</span>
        <label class="switch-row">
          <span class="switch-row__label">Mostra solo foto private</span>
          <span class="switch">
            <input id="filterPrivate" class="switch__input" type="checkbox" role="switch" aria-label="Mostra solo foto private" ${privateOnlyFilter ? "checked" : ""} />
            <span class="switch__thumb"></span>
          </span>
        </label>
      </div>`;
    qs<HTMLInputElement>("#filterPrivate").addEventListener("change", (e) => {
      privateOnlyFilter = (e.target as HTMLInputElement).checked;
      render();
    });
  }

  /** Dettaglio gruppo: anteprima grande della foto corrente + carousel thumb
   * orizzontale per cambiarla. Click sull'anteprima -> lightbox fullscreen. */
  function openGroup(group: PhotoSummary[]): void {
    const detail = qs<HTMLDivElement>("#detail");
    editingPosition = false;
    detail.classList.remove("sheet--edit-location");
    let current = 0;

    const thumbsHtml = `<div class="thumb-carousel" role="list" aria-label="Foto del gruppo">
            ${group
              .map(
                (p, i) => `
              <button type="button" class="thumb-carousel__item" data-thumb="${i}" aria-label="Foto ${i + 1} di ${group.length}">
                <img src="${mediaUrl(p.id)}" alt="" loading="lazy" />
              </button>`,
              )
              .join("")}
          </div>`;

    const multi = group.length > 1;
    detail.innerHTML = `
      <div class="sheet__top">
        <div class="sheet__handle"></div>
        <div class="sheet__head">
          <strong class="sheet__title">${multi ? `${group.length} foto` : "Foto"}</strong>
          <button id="detailClose" class="icon-btn" title="Chiudi" aria-label="Chiudi">${icon("close", { label: "Chiudi" })}</button>
        </div>
      </div>
      <div class="detail-viewer">
        <div class="detail-viewer__media">
          <button type="button" id="detailMain" class="detail-viewer__main" aria-label="Apri foto a schermo intero">
            <img id="detailMainImg" class="detail-viewer__img img-fade" alt="" />
          </button>
          <div id="detailPriv" class="detail-viewer__priv"></div>
          <div class="detail-viewer__actions">
            <button id="detailEdit" class="icon-btn detail-viewer__action" title="Modifica" aria-label="Modifica">${icon("edit", { size: 20, label: "Modifica" })}</button>
            <button id="detailDel" class="icon-btn icon-btn--danger detail-viewer__action" title="Elimina" aria-label="Elimina">${icon("delete", { size: 20, label: "Elimina" })}</button>
          </div>
        </div>
        <figcaption class="photo-card__body">
          <span id="detailDesc" class="photo-card__desc"></span>
          <span id="detailPlace" class="photo-card__place"></span>
          <span id="detailDate" class="photo-card__date"></span>
          <div id="detailCats" class="chips chips--readonly"></div>
          <div id="detailGender" class="gender gender--readonly"></div>
        </figcaption>
        ${multi ? thumbsHtml : ""}
      </div>`;
    detail.hidden = false;
    updateScrim();

    function renderViewer(): void {
      const p = group[current];
      const date = formatDate(p.taken_at ?? p.created_at);
      const desc = p.description ? escapeHtml(p.description) : "";
      const priv = p.is_private === 1 ? `<span class="badge-private">${icon("lock", { size: 16 })} privata</span>` : "";
      const img = qs<HTMLImageElement>("#detailMainImg");
      img.classList.remove("img--error", "is-loaded");
      img.src = mediaUrl(p.id);
      img.alt = desc;
      qs<HTMLDivElement>("#detailPriv").innerHTML = priv;
      qs<HTMLSpanElement>("#detailDesc").textContent = desc;
      // Data con icona (vuoto -> :empty la nasconde).
      const dateEl = qs<HTMLSpanElement>("#detailDate");
      dateEl.innerHTML = date ? `${icon("event", { size: 16 })} ${escapeHtml(date)}` : "";
      // Luogo (non sensibile): parsing UI best-effort (Place-1). Primary con icona
      // location, context compatto con icona public sul livello più generale; CAP
      // e livelli intermedi rumorosi nascosti; stringa completa nel title.
      const placeEl = qs<HTMLSpanElement>("#detailPlace");
      const parsed = parsePlaceLabel(p.place_label);
      if (!parsed) {
        placeEl.innerHTML = "";
        placeEl.removeAttribute("title");
      } else {
        placeEl.title = parsed.full;
        const ctx = parsed.context
          .map((t, i) =>
            i === parsed.context.length - 1
              ? `${icon("public", { size: 14 })} ${escapeHtml(t)}`
              : escapeHtml(t),
          )
          .join(`<span class="photo-card__place-sep" aria-hidden="true">·</span>`);
        placeEl.innerHTML =
          `<span class="photo-card__place-primary">${icon("location", { size: 16 })} ${escapeHtml(parsed.primary)}</span>` +
          (ctx ? `<span class="photo-card__place-context">${ctx}</span>` : "");
      }
      // Categorie (non sensibili): chip read-only.
      qs<HTMLDivElement>("#detailCats").innerHTML = (p.categories ?? [])
        .map((v) => {
          const c = CATEGORIES.find((x) => x.value === v);
          return c
            ? `<span class="chip chip--readonly">${icon(c.icon, { size: 16 })}<span>${c.label}</span></span>`
            : "";
        })
        .join("");
      // Gender (private-only): presente solo se il backend lo ha ritornato.
      const genderIcons = [
        p.tag_male ? icon("male", { size: 18, label: "Maschile" }) : "",
        p.tag_female ? icon("female", { size: 18, label: "Femminile" }) : "",
      ].filter(Boolean);
      qs<HTMLDivElement>("#detailGender").innerHTML = genderIcons.join("");
      detail.querySelectorAll<HTMLButtonElement>("[data-thumb]").forEach((btn) => {
        btn.classList.toggle("thumb-carousel__item--active", Number(btn.dataset.thumb) === current);
      });
    }

    qs<HTMLButtonElement>("#detailClose").addEventListener("click", () => {
      detail.hidden = true;
      updateScrim();
    });
    // Fallback immagine senza handler inline (compatibile con CSP stretta).
    qs<HTMLImageElement>("#detailMainImg").addEventListener("error", () =>
      qs<HTMLImageElement>("#detailMainImg").classList.add("img--error"),
    );
    qs<HTMLImageElement>("#detailMainImg").addEventListener("load", () =>
      qs<HTMLImageElement>("#detailMainImg").classList.add("is-loaded"),
    );
    qs<HTMLButtonElement>("#detailMain").addEventListener("click", () => openLightbox(group, current));
    detail.querySelectorAll<HTMLButtonElement>("[data-thumb]").forEach((btn) => {
      btn.addEventListener("click", () => {
        current = Number(btn.dataset.thumb);
        renderViewer();
      });
    });
    qs<HTMLButtonElement>("#detailEdit").addEventListener("click", () => editPhoto(group[current]));
    qs<HTMLButtonElement>("#detailDel").addEventListener("click", () => confirmDelete(group[current]));

    renderViewer();
  }

  // --- Lightbox / gallery fullscreen ---
  // Apre la foto ingrandita; naviga nel gruppo (prev/next), chiude con il bottone,
  // click sullo sfondo o ESC. Usa l'endpoint media gated: nessun leak privato.
  function openLightbox(group: PhotoSummary[], startIndex: number): void {
    const box = qs<HTMLDivElement>("#lightbox");
    let i = startIndex;

    const drawCurrent = (): void => {
      const p = group[i];
      const desc = p.description ? escapeHtml(p.description) : "";
      const date = formatDate(p.taken_at ?? p.created_at);
      const time = formatTime(p.taken_at);
      const meta = [date, time].filter(Boolean).join(" · ");
      const priv = p.is_private === 1 ? `${icon("lock", { size: 14 })} ` : "";
      const lbImg = qs<HTMLImageElement>("#lbImg");
      lbImg.classList.remove("img--error", "is-loaded");
      lbImg.src = mediaUrl(p.id);
      lbImg.alt = desc;
      qs<HTMLSpanElement>("#lbCounter").textContent = `${i + 1} / ${group.length}`;
      qs<HTMLDivElement>("#lbCaption").innerHTML = `
        ${desc ? `<span class="lightbox__desc">${desc}</span>` : ""}
        ${meta ? `<span class="lightbox__meta">${priv}${meta}</span>` : priv ? `<span class="lightbox__meta">${priv}privata</span>` : ""}`;
      qs<HTMLButtonElement>("#lbPrev").disabled = i <= 0;
      qs<HTMLButtonElement>("#lbNext").disabled = i >= group.length - 1;
    };

    box.innerHTML = `
      <div class="lightbox__bar">
        <span id="lbCounter" class="lightbox__counter"></span>
        <button id="lbClose" class="icon-btn" title="Chiudi" aria-label="Chiudi">${icon("close", { label: "Chiudi" })}</button>
      </div>
      <div class="lightbox__stage">
        <button id="lbPrev" class="lightbox__nav lightbox__nav--prev" aria-label="Foto precedente">${icon("chevron_left")}</button>
        <img id="lbImg" class="lightbox__img img-fade" alt="" />
        <button id="lbNext" class="lightbox__nav lightbox__nav--next" aria-label="Foto successiva">${icon("chevron_right")}</button>
      </div>
      <div id="lbCaption" class="lightbox__caption"></div>`;
    box.hidden = false;

    const go = (delta: number): void => {
      const next = i + delta;
      if (next < 0 || next >= group.length) return;
      i = next;
      drawCurrent();
    };

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    lightboxKeyHandler = onKey;

    function close(): void {
      box.hidden = true;
      box.innerHTML = "";
      document.removeEventListener("keydown", onKey);
      lightboxKeyHandler = null;
    }

    qs<HTMLImageElement>("#lbImg").addEventListener("error", () =>
      qs<HTMLImageElement>("#lbImg").classList.add("img--error"),
    );
    qs<HTMLImageElement>("#lbImg").addEventListener("load", () =>
      qs<HTMLImageElement>("#lbImg").classList.add("is-loaded"),
    );
    qs<HTMLButtonElement>("#lbClose").addEventListener("click", close);
    qs<HTMLButtonElement>("#lbPrev").addEventListener("click", () => go(-1));
    qs<HTMLButtonElement>("#lbNext").addEventListener("click", () => go(1));
    // Click sullo sfondo (non sull'immagine/controlli) chiude.
    box.addEventListener("click", (e) => {
      if (e.target === box || (e.target as HTMLElement).classList.contains("lightbox__stage")) close();
    });
    document.addEventListener("keydown", onKey);

    drawCurrent();
  }

  async function confirmDelete(p: PhotoSummary): Promise<void> {
    const confirmed = await confirmDialog("L'azione è reversibile solo dal database.");
    if (!confirmed) return;
    try {
      await deletePhoto(p.id);
      qs<HTMLDivElement>("#detail").hidden = true;
      updateScrim();
      await refresh();
    } catch (err) {
      setStatus(
        err instanceof ApiError && err.status === 404
          ? "Foto non disponibile."
          : "Errore durante l'eliminazione.",
      );
    }
  }

  function editPhoto(p: PhotoSummary): void {
    const detail = qs<HTMLDivElement>("#detail");
    detail.innerHTML = `
      <div class="sheet__top">
        <div class="sheet__handle"></div>
        <div class="sheet__head">
          <strong class="sheet__title">Modifica foto</strong>
          <button type="button" id="editCancel" class="icon-btn" title="Annulla" aria-label="Annulla">${icon("close", { label: "Annulla" })}</button>
        </div>
      </div>
      <form id="editForm" class="form">
        <label class="field">
          <span class="field__label">Descrizione</span>
          <textarea id="edDesc" class="field__control" rows="2">${p.description ? escapeHtml(p.description) : ""}</textarea>
        </label>
        <label class="field">
          <span class="field__label">Luogo</span>
          <input id="edPlace" class="field__input" type="text" maxlength="120" value="${p.place_label ? escapeHtml(p.place_label) : ""}" />
        </label>
        ${locationPickerMarkup()}
        <div class="field">
          <span class="field__label">Categorie</span>
          ${categorySelectMarkup("edCats", p.categories ?? [])}
        </div>
        <label class="field">
          <span class="field__label">Data scatto</span>
          <input id="edDate" class="field__input" type="datetime-local" value="${toDatetimeLocal(p.taken_at)}" />
        </label>
        ${
          privateMode.active
            ? `<label class="switch-row">
          <span class="switch-row__label">Privata</span>
          <span class="switch">
            <input id="edPrivate" class="switch__input" type="checkbox" role="switch" aria-label="Privata" ${p.is_private === 1 ? "checked" : ""} />
            <span class="switch__thumb"></span>
          </span>
        </label>
        <div class="field">
          <span class="field__label">Genere (privato)</span>
          <div id="edGender" class="gender">${genderToggles(p.tag_male === 1, p.tag_female === 1)}</div>
        </div>`
            : ""
        }
        <button type="submit" id="edSubmit" class="btn">Salva</button>
        <p id="edMsg" class="form__msg"></p>
      </form>`;
    editingPosition = true;
    detail.classList.add("sheet--edit-location");
    detail.hidden = false;
    updateScrim();

    const locationPicker = createLocationPicker(map, detail, qs<HTMLInputElement>("#edPlace"), p.latitude, p.longitude);
    editLocationPicker = locationPicker;

    function closeEdit(): void {
      locationPicker.destroy();
      editLocationPicker = null;
      editingPosition = false;
      detail.classList.remove("sheet--edit-location");
      detail.hidden = true;
      updateScrim();
    }

    qs<HTMLButtonElement>("#editCancel").addEventListener("click", closeEdit);
    enableSwipeToClose(detail, closeEdit);

    const edCatSelect = wireCategorySelect(qs<HTMLDivElement>("#edCats"), detail);
    const edGender = document.querySelector<HTMLDivElement>("#edGender");
    if (edGender) wireChips(edGender);

    const form = qs<HTMLFormElement>("#editForm");
    const msg = qs<HTMLParagraphElement>("#edMsg");
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const lat = locationPicker.getLat();
      const lng = locationPicker.getLng();
      if (lat === null || lng === null) {
        msg.textContent = "Imposta una posizione sulla mappa, con la ricerca o con la posizione attuale.";
        return;
      }
      const dateVal = qs<HTMLInputElement>("#edDate").value;
      const submit = qs<HTMLButtonElement>("#edSubmit");
      submit.disabled = true;
      msg.textContent = "Salvataggio…";
      try {
        const genderEl = document.querySelector<HTMLDivElement>("#edGender");
        await updatePhoto(p.id, {
          description: qs<HTMLTextAreaElement>("#edDesc").value.trim() || null,
          latitude: lat,
          longitude: lng,
          taken_at: dateVal ? dateVal : null,
          is_private: document.querySelector<HTMLInputElement>("#edPrivate")?.checked ?? false,
          categories: edCatSelect.getSelected(),
          place_label: qs<HTMLInputElement>("#edPlace").value.trim() || null,
          // Gender solo in private mode: il backend ignora comunque senza PM.
          ...(privateMode.active
            ? {
                tag_male: selectedChips(genderEl, "gender").includes("male"),
                tag_female: selectedChips(genderEl, "gender").includes("female"),
              }
            : {}),
        });
        closeEdit();
        await refresh();
      } catch (err) {
        submit.disabled = false;
        msg.textContent =
          err instanceof ApiError && err.status === 404
            ? "Foto non disponibile."
            : err instanceof ApiError && err.status === 400
              ? "Dati non validi."
              : "Errore durante il salvataggio.";
      }
    });
  }

  // Upload (coda multi-foto, Batch A) — controller estratto in src/lib/ui/upload.ts
  // (U1b, seguito al seam U1a). Comportamento invariato; renderApp passa solo
  // le dipendenze di contesto (mappa, private mode, refresh UI) come ports.
  const uploadController = mountUpload({
    map,
    isPrivateModeActive: () => privateMode.active,
    updateScrim,
    render,
    refresh,
    setStatus,
    prependPhoto: (photo) => {
      photos = [photo, ...photos];
    },
  });

  qs<HTMLButtonElement>("#uploadBtn").addEventListener("click", () => {
    if (uploadController.isOpen()) uploadController.close();
    else void uploadController.open();
  });

  /** Chiude e vuota (non solo `hidden`) tutti gli overlay che possono mostrare
   * contenuti privati: detail sheet, lightbox, upload, marker layer + stato
   * foto in memoria. Punto centrale unico, idempotente, per il cleanup forzato
   * su idle timeout e su lock manuale della private mode (evita di dover
   * sapere quale overlay è aperto). */
  function forceCloseAllOverlays(): void {
    const detail = qs<HTMLDivElement>("#detail");
    detail.hidden = true;
    detail.innerHTML = "";
    detail.classList.remove("sheet--edit-location");
    if (editLocationPicker) {
      editLocationPicker.destroy();
      editLocationPicker = null;
    }
    editingPosition = false;

    const box = qs<HTMLDivElement>("#lightbox");
    box.hidden = true;
    box.innerHTML = "";
    if (lightboxKeyHandler) {
      document.removeEventListener("keydown", lightboxKeyHandler);
      lightboxKeyHandler = null;
    }

    uploadController.close();

    if (markerLayer) {
      markerLayer.remove();
      markerLayer = null;
    }
    photos = [];

    updateScrim();
  }

  /** Idle timeout (10 minuti senza attività reale): logout completo, identico
   * nell'effetto al click su "Esci". La sessione e la private mode condividono
   * lo stesso idle timeout server-side (vedi functions/_middleware.ts), quindi
   * un idle timeout lato client implica sempre il logout dell'intera sessione,
   * non solo il lock della private mode. */
  async function handleIdleTimeout(): Promise<void> {
    teardown = true;
    forceCloseAllOverlays();
    try {
      await logout();
    } finally {
      renderLogin("Sessione scaduta per inattività.");
    }
  }

  renderPrivateControl();
  renderFilterPrivacyField();
  refresh();
  stopIdleWatch = startIdleWatch(() => {
    void handleIdleTimeout();
  });
}

// Registrazione service worker (PWA) solo in produzione.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

boot();
